import { configureStore } from "@reduxjs/toolkit";
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist";
import storage from "redux-persist/lib/storage";

import authReducer from "./slices/authSlice";
import notificationReducer from "./slices/notificationSlice";
import userReducer from "./slices/userSlice";
import formulaReducer from "./slices/formulaSlice";
import payrollReducer from "./slices/payrollSlice";
import cityReducer from "./slices/citySlice";
import historyReducer from "./slices/historySlice";
import variableReducer from "./slices/variablesSlice";
import departmentReducer from "./slices/departmentSlice";
import locationReducer from "./slices/locationSlice";
import reportReducer from "./slices/reportSlice";

/*
 * IMPORTANT:
 *
 * userReducer is NOT persisted.
 *
 * Authentication is handled by HttpOnly cookies.
 *
 * Therefore:
 * - access token -> HttpOnly cookie
 * - refresh token -> HttpOnly cookie
 * - user/role -> Redux only
 *
 * On F5, App.js calls /Me to restore the authenticated user.
 */

const persistConfig = {
  key: "root",
  storage,
  whitelist: [
    // Add non-auth state here if you actually want it persisted.
  ],
};

const persistedReducers = {
  auth: authReducer,
  notification: notificationReducer,

  // DO NOT persist user/auth state.
  user: userReducer,

  formula: formulaReducer,
  payroll: payrollReducer,
  city: cityReducer,
  history: historyReducer,
  variable: variableReducer,
  department: departmentReducer,
  location: locationReducer,
  report: reportReducer,
};

const rootReducer = (state, action) => {
  /*
   * When logging out, completely remove auth state.
   */
  if (action.type === "user/clearAuthState") {
    state = {
      ...state,
      user: undefined,
      auth: undefined,
    };
  }

  return {
    auth: persistedReducers.auth(state?.auth, action),

    notification: persistedReducers.notification(state?.notification, action),

    user: persistedReducers.user(state?.user, action),

    formula: persistedReducers.formula(state?.formula, action),

    payroll: persistedReducers.payroll(state?.payroll, action),

    city: persistedReducers.city(state?.city, action),

    history: persistedReducers.history(state?.history, action),

    variable: persistedReducers.variable(state?.variable, action),

    department: persistedReducers.department(state?.department, action),

    location: persistedReducers.location(state?.location, action),

    report: persistedReducers.report(state?.report, action),
  };
};

/*
 * If you already use redux-persist for other application data,
 * you can use this persisted reducer.
 *
 * Currently whitelist is empty because authentication should NOT
 * be persisted.
 */
const persistedRootReducer = persistReducer(persistConfig, rootReducer);

const store = configureStore({
  reducer: persistedRootReducer,

  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
});

export const persistor = persistStore(store);

export default store;
